import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import { acceptTermsAndConditions, fetchTermsAndConditions, downloadTermsDocument } from "../../services/terms_service";
import Snackbar from "../../scripts/snackbar";

/**
 * Simple RTF to React parser
 * Converts basic RTF formatting to React elements
 */
function parseRTF(rtfString) {
  if (!rtfString) return null;
  const backslashPlaceholder = "\uE000";

  // Remove RTF header and footer
  let text = rtfString
    .replace(/\{\\rtf1[^}]*}/g, "") // Remove RTF header
    .replace(/\{\\fonttbl[^}]*}/g, "") // Remove font table
    .replace(/\{\\colortbl[^}]*}/g, "") // Remove color table
    .replace(/\\f\d+/g, "") // Remove font references
    .replace(/\\fs\d+/g, "") // Remove font size
    .replace(/\\b\d?/g, "") // Remove bold
    .replace(/\\i\d?/g, "") // Remove italic
    .replace(/\\u\d+/g, "") // Remove unicode
    .replace(/[{}]/g, "") // Remove remaining braces
    .replace(/\\par\s*/g, "\n\n") // Convert \par to double newline
    .replace(/\\'/g, "'") // Unescape quotes
    .replace(/\\\\/g, backslashPlaceholder) // Preserve escaped backslashes without double-unescaping
    .replace(new RegExp(backslashPlaceholder, "g"), "\\")
    .trim();

  // Split by double newlines to create paragraphs
  const paragraphs = text.split("\n\n").filter((p) => p.trim());

  return (
    <>
      {paragraphs.map((para, index) => (
        <Typography key={index} paragraph sx={{ textAlign: "justify", mb: 2 }}>
          {para.trim()}
        </Typography>
      ))}
    </>
  );
}

/**
 * Terms and Conditions Acceptance Dialog
 * Displays T&C document and allows user to accept or decline
 */
export default function TermsAndConditionsDialog({ open, termsData, backend, onAccept, onDecline, type = "platform" }) {
  const [termsDocument, setTermsDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false);

  const loadTermsDocument = useCallback(async () => {
    setIsLoading(true);
    try {
      const doc = await fetchTermsAndConditions(termsData.terms_id, backend.fetch);
      setTermsDocument(doc);
    } catch (error) {
      console.error("Failed to load terms and conditions:", error);
      Snackbar.error("Failed to load terms and conditions");
    } finally {
      setIsLoading(false);
    }
  }, [termsData, backend.fetch]);

  useEffect(() => {
    if (open && termsData) {
      loadTermsDocument();
    }
  }, [open, termsData, loadTermsDocument]);

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await acceptTermsAndConditions(termsData.terms_id, backend.fetch);
      Snackbar.success("Terms and conditions accepted");
      onAccept();
    } catch (error) {
      console.error("Failed to accept terms and conditions:", error);
      Snackbar.error("Failed to accept terms and conditions");
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = () => {
    setShowDeclineConfirmation(true);
  };

  const handleConfirmDecline = () => {
    setShowDeclineConfirmation(false);
    Snackbar.info("You must accept the terms and conditions to continue");
    onDecline();

    // Reload the page for commissioning T&C to reset state
    if (type === "commissioning") {
      window.location.reload();
    }
  };

  const handleCancelDecline = () => {
    setShowDeclineConfirmation(false);
  };

  const handleDownload = async () => {
    try {
      const blob = await downloadTermsDocument(termsData.terms_id, backend.fetch);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = termsDocument?.title;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download terms document:", error);
      Snackbar.error("Failed to download document");
    }
  };

  return (
    <>
      <Dialog open={open} maxWidth="md" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ textAlign: "center" }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: "bold", mb: 1 }}>
            {type === "commissioning" ? "Commissioning Terms & Conditions" : "Platform Terms & Conditions"}
          </Typography>
          {termsDocument ? termsDocument.title : "Terms and Conditions"}
          {termsDocument && termsDocument.version && (
            <Typography variant="caption" display="block" color="text.secondary">
              Version: {termsDocument.version}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : termsDocument ? (
            <Box>
              {/* Display RTF/text content */}
              {termsDocument.doc_text_rtf && (
                <Box
                  sx={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    mb: 2,
                    p: 3,
                    backgroundColor: "background.default",
                    borderRadius: 1,
                    lineHeight: 1.8,
                    "& p": {
                      marginBottom: 2,
                    },
                  }}
                >
                  {parseRTF(termsDocument.doc_text_rtf)}
                </Box>
              )}

              {/* PDF download link */}
              {termsDocument.pdf_url && (
                <Box mt={2}>
                  <Link component="button" onClick={handleDownload} underline="hover" sx={{ cursor: "pointer" }}>
                    Download PDF Version
                  </Link>
                </Box>
              )}

              {/* Date information */}
              {(termsDocument.from_date || termsDocument.to_date) && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    {termsDocument.from_date && `Effective from: ${termsDocument.from_date}`}
                    {termsDocument.to_date && ` | Valid until: ${termsDocument.to_date}`}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="error">Failed to load terms and conditions</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDecline} variant="outlined" color="error" disabled={isAccepting}>
            Decline
          </Button>
          <Button
            onClick={handleAccept}
            variant="contained"
            color="primary"
            disabled={isLoading || isAccepting}
            startIcon={isAccepting && <CircularProgress size={16} />}
          >
            {isAccepting ? "Accepting..." : "Accept"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline Confirmation Dialog */}
      <Dialog open={showDeclineConfirmation} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
            Are you sure?
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography>
            If you don't accept the Terms & Conditions, you won't be able to continue on the platform.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDecline} variant="outlined" color="primary">
            Go Back
          </Button>
          <Button onClick={handleConfirmDecline} variant="contained" color="error">
            Decline Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
